<template>
  <div>
    <h2>Delete/Scramble User Accounts</h2>
    <template v-if="hasScramblePermission">
      <div class="well">
        <form class="form" @submit.prevent="uploadCSV">
          <div class="form-group">
            <label for="csvToScramble">CSV of Users</label>
            <input
              type="file"
              ref="fileInput"
              accept="text/csv"
              id="csvToScramble"
              @change="fileChanged">
            <p class="help-block">Expects a CSV provided by the Expired/Inactive Users Export - mandatory columns are uuid, email, and domain_uuid.</p>
          </div>

          <div class="form-group">
            <label for="scrambleNotes">Notes (required)</label>
            <textarea
              v-model="csvNotes"
              class="form-control"
              name="scrambleNotes"
              id="scrambleNotes"
              cols="30"
              rows="3"/>
            <p class="help-block">Description of the contents of CSV (e.g. date cut-off info, enterprise vs. trial users, etc)</p>
          </div>

          <alert
            v-if="uploadErr"
            type="danger"
            dismissible
            @dismissed="uploadErr = ''">{{ uploadErr }}</alert>

          <alert
            v-if="uploadSuccess"
            type="success"
            dismissible
            @dismissed="uploadSuccess = ''">{{ uploadSuccess }}</alert>

          <button
            class="btn btn-primary"
            type="submit"
            :disabled="!csvFile || !csvNotes || uploading">
            <i v-if="uploading" class="fa fa-circle-o-notch fa-spin"/>
            Scramble Users in CSV
          </button>
        </form>
      </div>
    </template>

    <h3 class="light">Scramble Records
      <button
        class="btn btn-default"
        @click="getRecords"
        :disabled="fetchingRecords">
        <i class="fa fa-circle-o-notch fa-spin text-muted" v-if="fetchingRecords"/>
        Refresh
      </button>
    </h3>
    <alert
      v-if="!fetchingRecords && records.length === 0"
      type="info"
    >No scramble records found</alert>
    <alert
      v-if="recordsErr"
      type="danger"
      dismissible
      @dismissed="recordsErr = ''">{{ recordsErr }}</alert>

    <v-client-table
      v-if="records.length > 0"
      ref="scrambleRecordsTable"
      :data="recordRows"
      :columns="tableInfo.columns"
      :options="tableInfo.options">
      <template slot="filename" slot-scope="props">
        {{ props.row.filename }}
        <br>
        <p><small class="text-muted">{{ props.row.uuid }}</small></p>
        <p v-if="props.row.created_by || props.row.notes">
          {{ props.row.created_by }}<br>
          {{ props.row.notes }}
        </p>
      </template>
      <template slot="timestamp" slot-scope="props">
        {{ (new Date(props.row.started_at)).toLocaleString() }}
        <br>
        <span v-if="props.row.duration" class="text-muted"><i class="fa fa-clock-o"/> {{ props.row.duration }}</span>
      </template>
      <template slot="mc_batch_id" slot-scope="props">
        <a
          v-if="props.row.mc_batch_id"
          role="button"
          @click.prevent="showMailchimpStatus(props.row.uuid, props.row.mc_batch_id)">{{ props.row.mc_batch_id }}</a>
      </template>
      <template slot="userstore_delete_op_id" slot-scope="props">
        <a
          v-if="props.row.userstore_delete_op_id"
          role="button"
          @click.prevent="showUserstoreDeleteOp(props.row.uuid, props.row.userstore_delete_op_id)">View status</a>
      </template>
      <template slot="status" slot-scope="props">
        <small v-if="props.row.status === 'Finished'" class="text-success">
          <i class="fa fa-check-circle"/> Completed
        </small>
        <small v-else>{{ props.row.status }}</small>
        <button
          class="btn btn-default btn-xs"
          aria-label="refresh status"
          @click="getRecordDetail(props.row.uuid)"
          :disabled="fetchingDetail[props.row.uuid]"><i class="fa fa-refresh" aria-hidden="true" /></button>
        <br>
        <a role="button" @click="displayModal('Logs', props.row.log)">View log</a>
        <br>
        <a
          v-if="props.row.errors && props.row.errors.length > 0"
          role="button"
          @click="displayModal('Errors', props.row.errors)">View errors</a>
      </template>
    </v-client-table>

    <modal
      v-model="showDetailModal"
      :title="modalTitle"
      :footer="false"
      @hide="showDetailModal = false">
      <pre>{{ JSON.stringify(modalJSON, '', 2) }}</pre>
    </modal>
  </div>
</template>

<script>
import { ClientTable } from 'vue-tables-2'
import { Tooltip, Alert, Modal } from 'uiv'
import { Permissions } from '../client_auth/permissions'

export default {
  name: 'UserScramblePage',
  components: { ClientTable, Tooltip, Alert, Modal },
  data() {
    return {
      hasScramblePermission: false,

      uploading: false,
      csvFile: null,
      csvNotes: '',
      uploadErr: '',
      uploadSuccess: '',

      fetchingRecords: false,
      fetchingDetail: {}, // uuid -> true/false
      records: [],
      recordDetails: {},
      recordsErr: '',

      showDetailModal: false,
      modalTitle: '',
      modalJSON: null,

      tableInfo: {
        columns: [
          'filename',
          'timestamp',
          'num_users',
          'num_domains',
          'mc_batch_id',
          'userstore_delete_op_id',
          'status'
        ],
        options: {
          headings: {
            filename: 'CSV file',
            timestamp: 'Timestamp',
            num_users: '# Users',
            num_domains: '# Domains',
            mc_batch_id: 'Mailchimp Batch',
            userstore_delete_op_id: 'User Cache Removal',
            status: 'Status'
          },
          sortable: ['filename', 'timestamp', 'num_users', 'num_domains', 'status'],
          filterable: false
        }
      }
    }
  },
  computed: {
    recordRows() {
      return this.records.map(r => this.recordDetails[r.uuid])
    }
  },
  methods: {
    async uploadCSV() {
      if (!confirm('Are you sure you want to scramble the users in the selected CSV?')) {
        return
      }

      this.uploadErr = ''
      this.uploadSuccess = ''
      this.uploading = true
      try {
        await this.$angularWorld.adminApi.uploadScrambleCSV({
          file: this.csvFile,
          notes: this.csvNotes
        })
        this.$refs.fileInput.value = ''
        this.uploadSuccess = 'Scramble operation started. Check status below.'
        this.csvFile = null
        this.csvNotes = ''
        this.getRecords()
      } catch (err) {
        this.uploadErr = err
      } finally {
        this.uploading = false
      }
    },
    fileChanged(evt) {
      this.csvFile = evt.target.files[0]
    },
    async getRecords() {
      this.fetchingRecords = true
      this.recordsErr = ''

      try {
        this.records = await this.$angularWorld.adminApi.getScrambleRecords({
          limit: 100
        })
        this.records &&
          this.records.forEach(r => {
            this.recordDetails[r.uuid] = r
          })
      } catch (err) {
        this.recordsErr = err
      } finally {
        this.fetchingRecords = false
      }
    },
    async getRecordDetail(recordID) {
      this.$set(this.fetchingDetail, recordID, true)
      try {
        let record = await this.$angularWorld.adminApi.getScrambleRecord(recordID)
        this.recordDetails = {
          ...this.recordDetails,
          [recordID]: record
        }
      } catch (err) {
        console.error(`Error getting record detail: ${err}`)
        this.displayModal('Error fetching record detail', err)
      } finally {
        this.$set(this.fetchingDetail, recordID, false)
      }
    },
    async showMailchimpStatus(recordID) {
      let record = this.recordDetails[recordID]
      if (!record.mc_batch_status) {
        await this.getRecordDetail(recordID)
      }

      this.displayModal(
        `Mailchimp Status ${record.mc_batch_id}`,
        this.recordDetails[recordID].mc_batch_status
      )
    },
    async showUserstoreDeleteOp(recordID) {
      let record = this.recordDetails[recordID]
      if (!record.userstore_delete_op_record) {
        await this.getRecordDetail(recordID)
      }

      this.displayModal(
        'User Cache Removal',
        this.recordDetails[recordID].userstore_delete_op_record
      )
    },
    displayModal(title, content) {
      this.modalTitle = title
      this.modalJSON = content
      this.showDetailModal = true
    }
  },
  async created() {
    this.getRecords();
    this.hasScramblePermission = await this.$angularWorld.auth.doesUserHavePermission([Permissions.ScrambleOps])
  }
}
</script>

<style lang="less" scoped>
h2 {
  margin-top: 32px;
  margin-bottom: 16px;
  font-size: 24px;
}
h3 {
  font-size: 21px;
}
pre {
  white-space: pre-wrap;
}
</style>
